import type { DayForecast } from '@lemontaigu/staff';

// « Prévisionnel » — what the saved load implies for the room-cleaning shift,
// shown ABOVE the activity Gantt in both the Jour and Semaine views
// (operator request 2026-08-01).
//
// It answers three questions the Gantt alone cannot: WHEN the shift should run
// today, HOW MUCH work that is in a unit the desk can picture (à-blanc
// equivalents), and HOW MANY people it needs. The planned window sits next to
// it so the difference is visible without arithmetic, and a single button
// aligns the planned window to the forecast — writing a PER-DAY override, never
// the activity's default (the window is supposed to move each day; flattening
// it into the default would defeat the model).

/** The planned window for the activity on this day (override ⊕ default). */
export interface PlannedWindow {
  windowStart: string;
  windowEnd: string;
}

/** One decimal, French-style — « 42,3 à-blancs équivalents ». */
function fmtEquivalents(value: number): string {
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Why this many people — surfaced because « 3 pers » with no reason invites the
 * desk to overrule it. Which of the two rules BINDS is the useful fact:
 * the volume, or the operator's « two is easier » floor.
 */
function headsReason(f: DayForecast): string {
  if (f.comfortFloor > f.capacityHeads) return `confort (${f.capacityHeads} suffirait pour le volume)`;
  if (f.capacityHeads > f.comfortFloor) return 'volume de travail';
  return 'volume et confort';
}

export function PrevisionnelStrip({
  forecasts,
  plannedFor,
  hasOverride,
  onApply,
  applyingId,
  canApply,
}: {
  forecasts: DayForecast[];
  /** Planned window for an activity, or null when it has none. */
  plannedFor: (activityId: number) => PlannedWindow | null;
  /**
   * Does this day carry an explicit per-day override for the activity?
   *
   * This is what separates « never aligned » from « aligned, then the load
   * moved » — the operator's question when a group books 12 rooms after the
   * shift was set. An override means somebody deliberately fixed that window;
   * if it no longer matches the forecast, the forecast has since changed under
   * it, and that deserves a warning rather than the same neutral button an
   * untouched day gets.
   */
  hasOverride?: (activityId: number) => boolean;
  /** Align the planned window to the forecast, as a per-day override. */
  onApply?: (f: DayForecast) => void;
  /** Activity id currently being written, for the busy state. */
  applyingId?: number | null;
  /** False hides the apply button (no rh.board.write) — the figures still show. */
  canApply?: boolean;
}) {
  if (forecasts.length === 0) return null;

  return (
    <div className="previsionnel">
      <div className="previsionnel-head">
        <strong>Prévisionnel</strong>
        <span className="previsionnel-hint">calculé d’après la charge saisie</span>
      </div>
      <ul className="previsionnel-list">
        {forecasts.map((f) => {
          const planned = plannedFor(f.activityId);
          const aligned =
            planned !== null &&
            planned.windowStart.slice(0, 5) === f.windowStart &&
            planned.windowEnd.slice(0, 5) === f.windowEnd;
          // Deliberately set for this day, yet no longer matching what the load
          // now implies — the « a group booked 12 rooms » case.
          const drifted = !aligned && (hasOverride?.(f.activityId) ?? false);
          return (
            <li key={f.activityId} className="previsionnel-row">
              <span className="previsionnel-label">{f.label}</span>
              <span className="previsionnel-window">
                {f.windowStart}–{f.windowEnd}
              </span>
              <span className="previsionnel-load">
                {fmtEquivalents(f.aBlancEquivalents)} à-blancs éq.
              </span>
              <span className="previsionnel-heads">
                {f.heads} pers.
                <span className="previsionnel-why"> · {headsReason(f)}</span>
              </span>
              {planned && !aligned && (
                <span className="previsionnel-planned">
                  planifié {planned.windowStart.slice(0, 5)}–{planned.windowEnd.slice(0, 5)}
                </span>
              )}
              {!aligned && drifted && (
                <span className="previsionnel-drift" title="L’horaire de ce jour avait été fixé ; la charge a changé depuis.">
                  ⚠ le prévisionnel a changé depuis
                </span>
              )}
              {aligned ? (
                <span className="previsionnel-aligned">✓ aligné</span>
              ) : (
                canApply &&
                onApply && (
                  <button
                    type="button"
                    className={`previsionnel-apply${drifted ? ' is-drifted' : ''}`}
                    disabled={applyingId === f.activityId}
                    title="Aligne l’horaire planifié de cette activité sur le prévisionnel, pour ce jour uniquement"
                    onClick={() => onApply(f)}
                  >
                    {applyingId === f.activityId ? '…' : drifted ? 'Réaligner' : 'Appliquer au planning du jour'}
                  </button>
                )
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
